import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import Typo from "@/src/components/atom/Typo";
import Button from "@/src/components/atom/Button/BasicButton";
import Icon from "@/src/components/atom/Icon";
import colorSet from "@/src/styles/color";
import logoExport from "@/src/assets/logos/logo-export.svg";
import logoExportDev from "@/src/assets/logos/logo-export-dev.svg";
import CloseSvg from "@/src/assets/icons/icon-close-black.svg";
import GlobalLanguageSvg from "@/src/assets/icons/icon-global-language.svg";
import { isDevelopment } from "@/src/utils/environment";

interface MobileMenuPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onStartTrial: () => void;
  onNavigate: (section: string) => void;
  onLanguageChange: () => void;
  currentLanguage: string;
}

/**
 * 모바일 메뉴 패널 컴포넌트 (640px 이하)
 * Figma: node-id 194:70527
 *
 * @description
 * - Portal을 사용한 전체 화면 오버레이
 * - 메뉴 항목: Features, Pricing, Contact, Language
 * - ESC 키로 닫기 지원
 * - Body 스크롤 락
 */
const MobileMenuPanel: React.FC<MobileMenuPanelProps> = ({
  isOpen,
  onClose,
  onStartTrial,
  onNavigate,
  onLanguageChange,
  currentLanguage,
}) => {
  const { t } = useTranslation(["mainPage", "common"]);

  // ESC 키로 닫기
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Body 스크롤 락
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const menuItems = [
    { key: "features", label: t("mainPage:nav.a.features") },
    { key: "contact", label: t("mainPage:nav.a.contact") },
  ];

  const handleMenuClick = (section: string) => {
    onNavigate(section);
    onClose();
  };

  return createPortal(
    <Overlay $isOpen={isOpen} onClick={onClose}>
      <Panel $isOpen={isOpen} onClick={(e) => e.stopPropagation()}>
        {/* Header: Logo + Button + Close */}
        <Header>
          <LogoContainer>
            <LogoImage
              src={isDevelopment ? logoExportDev : logoExport}
              alt="ECOYA EXPORT"
            />
          </LogoContainer>

          <Actions>
            <Button
              buttonGrade="primary"
              buttonColor="blue"
              buttonSize={40}
              onClick={() => {
                onStartTrial();
                onClose();
              }}
            >
              {t("mainPage:nav.button.signup")}
            </Button>

            <CloseButton
              type="button"
              onClick={onClose}
              aria-label="Close menu"
            >
              <Icon iconSrc={CloseSvg} iconSize={24} />
            </CloseButton>
          </Actions>
        </Header>

        {/* Menu Items */}
        <MenuList>
          {menuItems.map((item) => (
            <MenuItem
              key={item.key}
              type="button"
              onClick={() => handleMenuClick(item.key)}
            >
              <Typo typoType="b4m" color="gray2">
                {item.label}
              </Typo>
            </MenuItem>
          ))}

          {/* Language Selector */}
          <LanguageButton type="button" onClick={onLanguageChange}>
            <Icon iconSrc={GlobalLanguageSvg} iconSize={22} />
            <Typo typoType="b4m" color="gray2">
              {currentLanguage === "en" ? "En" : "Ko"}
            </Typo>
          </LanguageButton>
        </MenuList>
      </Panel>
    </Overlay>,
    document.body,
  );
};

export default MobileMenuPanel;

// Styled Components

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transition: opacity 0.3s ease-in-out;
`;

const Panel = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background-color: ${colorSet.white};
  border-bottom: 1px solid #e7edf0;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  transform: ${({ $isOpen }) =>
    $isOpen ? "translateY(0)" : "translateY(-100%)"};
  transition: transform 0.3s ease-in-out;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 40px;
`;

const LogoImage = styled.img`
  height: 18px;
  width: 137px;
  display: block;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const CloseButton = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`;

const MenuList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const MenuItem = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  width: 100%;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`;

const LanguageButton = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`;
